import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  Switch,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import siteStatusService, {
  MaintenanceWindow,
  MaintenanceGateKey,
  MaintenanceGateMeta,
} from '../services/siteStatus.service';

type FilterTab = 'active' | 'upcoming' | 'past' | 'all';

const TYPE_LABELS: Record<string, string> = {
  full: 'کامل (تمام عملیات حساس)',
  partial: 'بخشی',
};

const formatDate = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const toDatetimeLocal = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const MaintenanceWindowManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<FilterTab>('active');
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [gates, setGates] = useState<MaintenanceGateMeta[]>([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MaintenanceWindow | null>(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'full' | 'partial'>('partial');
  const [selectedGates, setSelectedGates] = useState<MaintenanceGateKey[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [messageFa, setMessageFa] = useState('');
  const [allowAdminBypass, setAllowAdminBypass] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [winRes, gateRes] = await Promise.all([
        siteStatusService.listWindows(tab),
        gates.length ? Promise.resolve(gates) : siteStatusService.listGateCatalog(),
      ]);
      setWindows(winRes?.docs || winRes?.data || []);
      if (!gates.length) setGates(gateRes as MaintenanceGateMeta[]);
    } catch {
      enqueueSnackbar('خطا در دریافت پنجره‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab, gates.length, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setTitle('');
    setType('partial');
    setSelectedGates([]);
    const inOneHour = new Date(Date.now() + 60 * 60 * 1000);
    const inThreeHours = new Date(Date.now() + 3 * 60 * 60 * 1000);
    setStartsAt(toDatetimeLocal(inOneHour.toISOString()));
    setEndsAt(toDatetimeLocal(inThreeHours.toISOString()));
    setMessageFa(
      'سایت در حال به‌روزرسانی است. لطفاً تا اتمام عملیات (حدود ساعت X) از پرداخت و ثبت سفارش جدید خودداری کنید.'
    );
    setAllowAdminBypass(true);
    setFormOpen(true);
  };

  const openEdit = (w: MaintenanceWindow) => {
    setEditing(w);
    setTitle(w.title);
    setType(w.type);
    setSelectedGates(w.gateKeys || []);
    setStartsAt(toDatetimeLocal(w.startsAt));
    setEndsAt(toDatetimeLocal(w.endsAt));
    setMessageFa(w.messageFa);
    setAllowAdminBypass(w.allowAdminBypass);
    setFormOpen(true);
  };

  const submit = async () => {
    if (!title.trim() || !startsAt || !endsAt || !messageFa.trim()) {
      enqueueSnackbar('فیلدهای الزامی را پر کنید', { variant: 'warning' });
      return;
    }
    if (type === 'partial' && selectedGates.length === 0) {
      enqueueSnackbar('حداقل یک عملیات (gate) را برای پنجره بخشی انتخاب کنید', {
        variant: 'warning',
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        type,
        gateKeys: type === 'partial' ? selectedGates : [],
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        messageFa: messageFa.trim(),
        allowAdminBypass,
      };
      if (editing) {
        await siteStatusService.updateWindow(editing._id, payload);
        enqueueSnackbar('پنجره بروزرسانی شد', { variant: 'success' });
      } else {
        await siteStatusService.createWindow(payload);
        enqueueSnackbar('پنجره ایجاد شد', { variant: 'success' });
      }
      setFormOpen(false);
      load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'خطا در ذخیره', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const cancelWindow = async (w: MaintenanceWindow) => {
    const reason = window.prompt('دلیل لغو پنجره؟ (اختیاری)') || '';
    try {
      await siteStatusService.cancelWindow(w._id, reason || undefined);
      enqueueSnackbar('پنجره لغو شد', { variant: 'success' });
      load();
    } catch {
      enqueueSnackbar('خطا در لغو پنجره', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            مدیریت پنجره‌های به‌روزرسانی سایت
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            با تعریف یک پنجره به‌روزرسانی، کاربران در زمان مشخص نمی‌توانند پرداخت کنند، سفارش بدهند،
            یا ارسال ثبت کنند. به آنها پیامی نمایش داده می‌شود که سایت تا چه ساعتی در حال بروزرسانی
            است.
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
          پنجره جدید
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="active" label="فعال (هم‌اکنون)" />
        <Tab value="upcoming" label="در پیش رو" />
        <Tab value="past" label="گذشته/لغو شده" />
        <Tab value="all" label="همه" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : windows.length === 0 ? (
        <Alert severity="info">پنجره‌ای در این دسته یافت نشد.</Alert>
      ) : (
        <Grid container spacing={2}>
          {windows.map((w) => {
            const now = Date.now();
            const isActive =
              w.active && !w.cancelledAt &&
              new Date(w.startsAt).getTime() <= now &&
              new Date(w.endsAt).getTime() >= now;
            const isUpcoming = w.active && !w.cancelledAt && new Date(w.startsAt).getTime() > now;
            const isPast = new Date(w.endsAt).getTime() < now;

            return (
              <Grid item xs={12} md={6} key={w._id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: isActive
                      ? 'warning.main'
                      : isUpcoming
                      ? 'info.light'
                      : 'grey.300',
                    borderWidth: isActive ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <BuildIcon
                            fontSize="small"
                            color={isActive ? 'warning' : 'disabled'}
                          />
                          <Typography variant="h6" fontWeight={700}>
                            {w.title}
                          </Typography>
                          {isActive && <Chip label="در حال اجرا" color="warning" size="small" />}
                          {isUpcoming && (
                            <Chip label="در پیش رو" color="info" size="small" variant="outlined" />
                          )}
                          {w.cancelledAt && <Chip label="لغو شده" color="error" size="small" />}
                          {isPast && !w.cancelledAt && (
                            <Chip label="پایان یافته" size="small" variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {TYPE_LABELS[w.type]} · از {formatDate(w.startsAt)} تا{' '}
                          {formatDate(w.endsAt)}
                        </Typography>
                      </Box>
                      {!w.cancelledAt && !isPast && (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => openEdit(w)} title="ویرایش">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => cancelWindow(w)}
                            title="لغو"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    {w.type === 'partial' && (
                      <Stack direction="row" flexWrap="wrap" spacing={0.5} mb={1}>
                        {w.gateKeys.map((g) => {
                          const meta = gates.find((m) => m.key === g);
                          return (
                            <Chip
                              key={g}
                              label={meta?.nameFa || g}
                              size="small"
                              variant="outlined"
                            />
                          );
                        })}
                      </Stack>
                    )}

                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      پیام به کاربر: «{w.messageFa}»
                    </Typography>

                    {w.allowAdminBypass && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        ادمین‌ها با هدر <code>x-admin-bypass: 1</code> می‌توانند bypass کنند.
                      </Typography>
                    )}

                    {w.cancellationReason && (
                      <Alert severity="error" sx={{ mt: 1, fontSize: '0.85rem' }}>
                        دلیل لغو: {w.cancellationReason}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'ویرایش پنجره به‌روزرسانی' : 'پنجره جدید'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="عنوان داخلی"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder="مثلاً: انتقال DB به سرور جدید — جمعه شب"
            />

            <FormControl fullWidth>
              <InputLabel>نوع پنجره</InputLabel>
              <Select value={type} onChange={(e) => setType(e.target.value as any)} label="نوع پنجره">
                <MenuItem value="partial">بخشی — انتخاب عملیات خاص</MenuItem>
                <MenuItem value="full">کامل — تمام عملیات حساس</MenuItem>
              </Select>
            </FormControl>

            {type === 'partial' && (
              <FormControl fullWidth>
                <InputLabel>عملیات قفل‌شونده</InputLabel>
                <Select
                  multiple
                  value={selectedGates}
                  onChange={(e) =>
                    setSelectedGates(
                      typeof e.target.value === 'string'
                        ? (e.target.value.split(',') as MaintenanceGateKey[])
                        : (e.target.value as MaintenanceGateKey[])
                    )
                  }
                  label="عملیات قفل‌شونده"
                  renderValue={(selected) =>
                    (selected as MaintenanceGateKey[])
                      .map((k) => gates.find((g) => g.key === k)?.nameFa || k)
                      .join('، ')
                  }
                >
                  {gates.map((g) => (
                    <MenuItem key={g.key} value={g.key}>
                      <Checkbox checked={selectedGates.includes(g.key)} />
                      <ListItemText
                        primary={g.nameFa}
                        secondary={
                          <Typography variant="caption">
                            <code>{g.key}</code> — {g.descriptionFa}
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Stack direction="row" spacing={2}>
              <TextField
                label="شروع"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="پایان"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              label="پیام به کاربر"
              value={messageFa}
              onChange={(e) => setMessageFa(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 1000 }}
              required
            />

            <FormControlLabel
              control={
                <Switch
                  checked={allowAdminBypass}
                  onChange={(e) => setAllowAdminBypass(e.target.checked)}
                />
              }
              label="اجازه bypass به ادمین (برای تست در زمان maintenance)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>انصراف</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'در حال ذخیره...' : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceWindowManagement;
